import { Card } from "primereact/card";
import { ApiResponse } from "../../services/base/apiResponse";
import { DegreeEnum, StudentInfoAdminResponse, StudentPreviousDiplomaRequest } from "../../models/dtos";
import { Fieldset } from "primereact/fieldset";
import CustomInputText from "../../components/forms/CustomInputText";
import CustomDatepicker from "../../components/forms/CustomDatepicker";
import CustomDropdown from "../../components/forms/CustomDropdown";
import { schoolProfilesApi } from "../../services/nomenclatures/schoolProfilesApi";
import { universitySpecialitiesApi } from "../../services/nomenclatures/universitySpecialitiesApi";
import { universityFieldsApi } from "../../services/nomenclatures/universityFieldsApi";
import { universitiesApi } from "../../services/nomenclatures/universitiesApi";
import { schoolsApi } from "../../services/nomenclatures/schoolsApi";
import { citiesApi } from "../../services/nomenclatures/citiesApi";
import { countriesApi } from "../../services/nomenclatures/countriesApi";
import { studentsApi } from "../../services/students/studentsApi";
import { FormikProvider, useFormik } from "formik";
import { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useParams } from "react-router-dom";
import { degreesApi } from "../../services/nomenclatures/degreesApi";
import { Divider } from "primereact/divider";
import { format } from "date-fns";

const PreviousEducation = ({ student }: { student?: ApiResponse<StudentInfoAdminResponse> }) => {
  const toast = useRef<Toast>(null);
  const { data: diplomaCountries } = countriesApi.useGetCountriesQuery();
  const { data: diplomaCities } = citiesApi.useGetCitiesQuery();
  const { data: schools } = schoolsApi.useGetSchoolsQuery();
  const { data: universities } = universitiesApi.useGetUniversitiesQuery();
  const { data: universityFields } = universityFieldsApi.useGetUniversityFieldsQuery();
  const { data: universitySpecialities } = universitySpecialitiesApi.useGetUniversitySpecialitiesQuery();
  const { data: schoolProfiles } = schoolProfilesApi.useGetSchoolProfilesQuery();
  const { data: degrees } = degreesApi.useGetDegreesQuery();

  const [isEditing, setIsEditing] = useState(false);
  const { studentId } = useParams();

  const [
    updateStudentPreviousEducation,
    {
      isLoading: isUpdateStudentPreviousEducation,
      isError: isUpdateEducationError,
      isSuccess: isUpdateEducationSuccess,
    },
  ] = studentsApi.useUpdateEducationMutation();

  const handleClick = (studentId: any, diploma: Partial<StudentPreviousDiplomaRequest>) => {
    formik.setValues(diploma);
    setIsEditing(true);
  };

  const formik = useFormik({
    initialValues: {
      studentPreviousDiplomaId: null,
      serialNumber: "",
      registrationNumber: "",
      issuedOn: null,
      diplomaCountryId: null,
      diplomaCityId: null,
      schoolId: null,
      universityId: null,
      universityFieldId: null,
      universitySpecialityId: null,
      schoolProfileId: null,
      degreeId: null,
    } as Partial<StudentPreviousDiplomaRequest>,
    enableReinitialize: true,
    onSubmit: (values: Partial<StudentPreviousDiplomaRequest>) => {
      updateStudentPreviousEducation({ studentId: student?.data?.studentId!, request: values }).then((response) => {
        if ("data" in response && response.data) {
          toast.current!.show({ severity: "success", summary: "Успешно", detail: "Данните на студента са запазени." });
        }
      });
    },
  });

  const renderDiplomaCards = () => {
    return student?.data.previousEducation.map((diploma, index) => (
      <div key={index} className="p-col-12 p-md-6 p-lg-4 md-4 mt-4">
        <Fieldset legend={diploma.degreeName} style={{ marginBottom: "20px" }}>
          <Card title="" subTitle={`Профил: ${diploma.schoolProfileName ? diploma.schoolProfileName : "-"}`}>
            <div className="p-grid p-dir-col" style={{ padding: "20px" }}>
              <div className="p-col-12" style={{ marginBottom: "10px" }}>
                Учебно заведение:{" "}
                {diploma.degreeId === 4
                  ? (diploma.schoolName ?? diploma.schoolText)
                  : (diploma.universityName ?? diploma.universityText)}
              </div>
              <div className="p-col-12" style={{ marginBottom: "10px" }}>
                Издадена в:{" "}
                {`${diploma.diplomaCityName ? diploma.diplomaCityName : diploma.diplomaCityText ? diploma.diplomaCityText : "-"}, 
                ${diploma.diplomaCountryName ? diploma.diplomaCountryName : "-"}`}
              </div>
              {diploma.issuedOn && (
                <div className="p-col-12" style={{ marginBottom: "10px" }}>
                  Издадена на: {format(diploma.issuedOn, "dd.MM.yyyy")}
                </div>
              )}
              <div className="p-col-12" style={{ marginBottom: "10px" }}>
                Регистрационен номер: {diploma.registrationNumber ? diploma.registrationNumber : "-"}
              </div>
              <div className="p-col-12" style={{ marginBottom: "10px" }}>
                Сериен номер: {diploma.serialNumber ? diploma.serialNumber : "-"}
              </div>
              {diploma.graduationYear && (
                <div className="p-col-12" style={{ marginBottom: "10px" }}>
                  Година на завършване: {diploma.graduationYear ? diploma.graduationYear : "-"}
                </div>
              )}
            </div>
          </Card>
        </Fieldset>
        <div className="p-col-12" style={{ textAlign: "center", marginTop: "10px" }}>
          <Button
            icon="pi pi-pencil"
            iconPos="right"
            size="small"
            className="p-button-rounded p-button-secondary"
            label="Редактиране"
            onClick={() => handleClick(studentId, diploma)}
          />
        </div>
      </div>
    ));
  };

  const renderForm = () => {
    return (
      <div className="p-fluid formgrid grid">
        <FormikProvider value={formik}>
          <div className="col-12">
            <h4>{formik.values.studentPreviousDiplomaId ? "Редактиране на диплома" : "Добавяне на диплома"}</h4>
          </div>

          <div className="field col-12 md:col-12">
            <CustomInputText id="studentPreviousDiplomaId" label="" style={{ display: "none" }} />
          </div>
          <div className="field col-12 md:col-6">
            <CustomInputText id="serialNumber" label="Сериен номер" />
          </div>
          <div className="field col-12 md:col-6">
            <CustomInputText id="registrationNumber" label="Регистрационен номер" />
          </div>
          <div className="field col-12 md:col-6">
            <CustomDatepicker id="issuedOn" label="Издадена на" />
          </div>
          <div className="field col-12 md:col-6">
            <CustomDropdown
              id="degreeId"
              options={degrees?.data ?? []}
              optionLabel="name"
              optionValue="degreeId"
              placeholder="Изберете степен"
              label="Степен на обучение"
              showClear
            />
          </div>
          <div className="field col-12 md:col-4">
            <CustomDropdown
              id="diplomaCountryId"
              filter
              options={diplomaCountries?.data ?? []}
              optionLabel="name"
              optionValue="countryId"
              placeholder="Изберете държава"
              label="Държава на издаване"
              showClear
            />
          </div>
          <div className="field col-12 md:col-4">
            <CustomDropdown
              id="diplomaCityId"
              filter
              options={diplomaCities?.data ?? []}
              optionLabel={(option) => `${option.name} (общ. ${option.municipalityName})`}
              optionValue="cityId"
              placeholder="Изберете град"
              label="Град на издаване"
              showClear
            />
          </div>
          <div className="field col-12 md:col-4">
            <CustomInputText id="diplomaCityText" label="Град на издаване (свободен текст, за чужбина)" />
          </div>
          <Divider>Данни за средно образование</Divider>
          <div className="field col-12 md:col-4">
            <CustomDropdown
              id="schoolId"
              filter
              options={schools?.data ?? []}
              optionLabel="name"
              optionValue="schoolId"
              filterValue="cityId"
              filterField="diplomaCityId"
              placeholder="Изберете учебно заведение"
              label="Учебно заведение"
              showClear
            />
          </div>
          <div className="field col-12 md:col-4">
            <CustomDropdown
              id="schoolProfileId"
              filter
              options={schoolProfiles?.data ?? []}
              optionLabel="name"
              optionValue="schoolProfileId"
              placeholder="Изберете профил"
              label="Профил"
              showClear
            />
          </div>
          <div className="field col-12 md:col-4">
            <CustomInputText id="schoolText" label="Учебно заведение (свободен текст, за чужбина)" />
          </div>
          <Divider>Данни за висше образование</Divider>
          <div className="field col-12 md:col-4">
            <CustomDropdown
              id="universityId"
              filter
              options={universities?.data ?? []}
              optionLabel="name"
              optionValue="universityId"
              placeholder="Изберете университет"
              label="Университет"
              showClear
            />
          </div>
          <div className="field col-12 md:col-4">
            <CustomDropdown
              id="universityFieldId"
              filter
              options={universityFields?.data ?? []}
              optionLabel="name"
              optionValue="universityFieldId"
              placeholder="Изберете професионално направление"
              label="Професионално направление"
              showClear
            />
          </div>
          <div className="field col-12 md:col-4">
            <CustomInputText id="universityText" label="Университет (свободен текст, за чужбина)" />
          </div>

          <div className="field col-12 md:col-4">
            <CustomDropdown
              id="universitySpecialityId"
              filter
              options={universitySpecialities?.data ?? []}
              optionLabel="name"
              optionValue="universitySpecialityId"
              placeholder="Изберете специалност"
              label="Специалност"
              filterField="universityId"
              filterValue="universityId"
              showClear
            />
          </div>
          <div className="field col-12 md:col-4">
            <CustomInputText id="universitySpecialityText" label="Специалност (свободен текст, за чужбина)" />
          </div>

          <div className="col-12 mb-4">
            <Button
              label="Запазване"
              icon="pi pi-save"
              type="submit"
              onClick={() => formik.handleSubmit()}
              disabled={isUpdateStudentPreviousEducation}
            />{" "}
          </div>
        </FormikProvider>
      </div>
    );
  };

  return (
    <div>
      <div className="p-grid">{renderDiplomaCards()}</div>
      {(student?.data.previousEducation.length === 0 && !isEditing) || isEditing ? renderForm() : null}
      {/* {student?.data.previousEducation.length === 0 && !isEditing && (
        <div className="p-col-12" style={{ textAlign: 'center', marginTop: '10px' }}>
          <Button
            label="Добавяне на диплома"
            icon="pi pi-plus"
            onClick={handleCreate}
          />
        </div>
      )} */}
    </div>
  );
};

export default PreviousEducation;
